import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';

const URL = 'io-templates/api/v1/templates';

export type IoTemplateKind = 'MANAGED' | 'HYBRID' | 'DATA';

export interface IoTemplateColumn {
  field: string;
  label: string;
  visible: boolean;
  width: number;
  align?: 'left' | 'right' | 'center';
}

export interface IoTemplateSection {
  key: string;
  label: string;
  visible: boolean;
  body?: string;
}

export interface IoRate {
  value: number;
  type: 'percent' | 'absolute';
  /** Row label on the document, e.g. "VAT" or "Sales Tax". */
  label?: string;
}

/** Commercial settings applied to the schedule totals. */
export interface IoPricingSettings {
  currency: string;
  discount: IoRate;
  taxFirst: IoRate;
  taxSecond: IoRate;
  allowLineDiscount: boolean;
  showDiscountRow: boolean;
  showTaxRow: boolean;
}

export interface IoTemplate {
  _id: string;
  name: string;
  /** Stable identifier used in URLs, e.g. `io-managed-pmg`. */
  slug: string;
  kind: IoTemplateKind;
  pandaDocTemplateId?: string;
  isDefault?: boolean;
  /** Offered in the Raise IO dropdown. Absent on older templates, which count as enabled. */
  enabled?: boolean;
  columns: IoTemplateColumn[];
  sections: IoTemplateSection[];
  pricing: IoPricingSettings;
  updatedBy?: string;
  updatedAt?: string;
}

/** Layout definitions for Insertion Orders, rendered by NRVE rather than PandaDoc. */
export const ioTemplateApi = createApi({
  reducerPath: 'ioTemplateApi',
  baseQuery,
  tagTypes: ['IoTemplate'],
  endpoints: (builder) => ({
    listIoTemplates: builder.query<IoTemplate[], void>({
      query: () => URL,
      providesTags: ['IoTemplate']
    }),
    saveIoTemplate: builder.mutation<
      IoTemplate,
      {
        slug: string;
        columns?: IoTemplateColumn[];
        sections?: IoTemplateSection[];
        pricing?: IoPricingSettings;
        enabled?: boolean;
        isDefault?: boolean;
      }
    >({
      query: ({ slug, ...body }) => ({ url: `${URL}/${slug}`, method: 'PUT', body }),
      invalidatesTags: ['IoTemplate']
    }),
    /** Creates an NRVE-owned template, optionally copying an existing layout. */
    createIoTemplate: builder.mutation<IoTemplate, { name: string; copyFrom?: string }>({
      query: (body) => ({ url: URL, method: 'POST', body }),
      invalidatesTags: ['IoTemplate']
    }),
    deleteIoTemplate: builder.mutation<{ deleted: boolean }, string>({
      query: (slug) => ({ url: `${URL}/${slug}`, method: 'DELETE' }),
      invalidatesTags: ['IoTemplate']
    }),
    /**
     * Renders a template against sample content, including unsaved changes, so
     * the editor can show the effect of an edit as it is made.
     */
    previewIoTemplate: builder.mutation<
      string,
      {
        slug: string;
        columns?: IoTemplateColumn[];
        sections?: IoTemplateSection[];
        pricing?: IoPricingSettings;
        /** Render against this plan's real data instead of sample content. */
        mediaPlanId?: string;
      }
    >({
      query: ({ slug, ...body }) => ({
        url: `${URL}/${slug}/preview`,
        method: 'POST',
        body,
        responseHandler: (response) => response.text()
      })
    }),
    resetIoTemplate: builder.mutation<IoTemplate, string>({
      query: (slug) => ({ url: `${URL}/${slug}/reset`, method: 'POST' }),
      invalidatesTags: ['IoTemplate']
    })
  })
});

export const {
  useListIoTemplatesQuery,
  useSaveIoTemplateMutation,
  useResetIoTemplateMutation,
  useCreateIoTemplateMutation,
  useDeleteIoTemplateMutation,
  usePreviewIoTemplateMutation
} = ioTemplateApi;
